"use client";

import { ReactNode } from "react";
import { Info } from "lucide-react";

import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import { QuizOption, QuizQuestion } from "@/lib/utils/lomdaV3/types";
import { useLomdaLocale } from "../../../hooks/useLomdaLocale";
import { useResolvedInnerGap } from "../../../hooks/useGridGap";
import { ICON_SIZE } from "../../../lib/iconSizes";
import { DraftTextarea, nonBlank } from "../shared/draftTextarea";

const optionTextClass = "lomda-v3-text-small whitespace-pre-wrap break-words";
const openTextClass =
    "lomda-v3-text-p whitespace-pre-wrap break-words text-[hsl(var(--lomda-text))]";
const focusClass = "focus-within:border-[hsl(var(--lomda-border-active))]";

/** Parts of the theme inner gap: 16 / 12 / 6 / 4 px at the default 16. */
function useFeedbackGaps() {
    const inner = useResolvedInnerGap();
    return {
        box: inner,
        line: (inner * 3) / 4,
        icon: (inner * 3) / 8,
        label: inner / 4,
    };
}

/** A full-width line under the answer (an option card needs `flex-wrap`). */
const optionLineClass = (o: QuizOption) =>
    cn(
        "basis-full border-t",
        o.correct
            ? "border-[hsl(var(--lomda-success))]/30"
            : "border-[hsl(var(--lomda-destructive))]/30"
    );

const optionToneText = (o: QuizOption) =>
    o.correct
        ? "text-[hsl(var(--lomda-success))]"
        : "text-[hsl(var(--lomda-destructive))]";

export const optionFeedback = (o: QuizOption) =>
    o.feedback?.trim() || undefined;

function FeedbackIcon({ className }: { className: string }) {
    return (
        <span
            className={cn(
                "lomda-v3-text-small flex h-[1lh] shrink-0 items-center",
                className
            )}
        >
            <Info className={ICON_SIZE.feedbackIcon} />
        </span>
    );
}

export function OptionFeedback({ option }: { option: QuizOption }) {
    const gaps = useFeedbackGaps();
    const text = optionFeedback(option);
    if (!text) return null;
    return (
        <span
            className={cn(optionLineClass(option), "flex")}
            style={{ paddingTop: gaps.line, columnGap: gaps.icon }}
        >
            <FeedbackIcon className={optionToneText(option)} />
            <span className={cn(optionTextClass, "min-w-0 flex-1")}>
                {text}
            </span>
        </span>
    );
}

function OpenFeedbackBox({
    className,
    children,
}: {
    className?: string;
    children: ReactNode;
}) {
    const locale = useLomdaLocale();
    const gaps = useFeedbackGaps();
    return (
        <div
            className={cn(
                "flex flex-col rounded-[var(--radius)] border border-[hsl(var(--lomda-primary))]/15 bg-[hsl(var(--lomda-primary))]/5",
                className
            )}
            style={{ padding: gaps.box, rowGap: gaps.label }}
        >
            <span
                className="lomda-v3-text-small flex items-center text-[hsl(var(--lomda-muted-text))]"
                style={{ columnGap: gaps.icon }}
            >
                <FeedbackIcon className="text-[hsl(var(--lomda-primary))]" />
                {t("lomda-v3-editor.quiz.feedback", locale)}
            </span>
            {children}
        </div>
    );
}

/** Open questions keep their feedback in `explanation`. */
export function OpenFeedback({ question }: { question: QuizQuestion }) {
    const text = nonBlank(question.explanation)?.trimEnd();
    if (!text) return null;
    return (
        <OpenFeedbackBox>
            <p className={openTextClass}>{text}</p>
        </OpenFeedbackBox>
    );
}

export function OptionFeedbackInput({
    option,
    letter,
    onChange,
}: {
    option: QuizOption;
    letter: string;
    onChange: (feedback: string | undefined) => void;
}) {
    const gaps = useFeedbackGaps();
    return (
        <div
            className={cn(optionLineClass(option), focusClass)}
            style={{ paddingTop: gaps.line }}
        >
            <DraftTextarea
                value={option.feedback}
                onCommit={onChange}
                placeholder={t("lomda-v3-editor.quiz.feedback")}
                aria-label={t(
                    "lomda-v3-editor.quiz.option-feedback",
                    undefined,
                    {
                        letter,
                    }
                )}
                className={optionTextClass}
            />
        </div>
    );
}

export function OpenFeedbackInput({
    question,
    onChange,
}: {
    question: QuizQuestion;
    onChange: (explanation: string) => void;
}) {
    const placeholder = t("lomda-v3-editor.quiz.feedback");
    return (
        <OpenFeedbackBox className={focusClass}>
            <DraftTextarea
                value={question.explanation}
                onCommit={(next) => onChange(next ?? "")}
                placeholder={placeholder}
                aria-label={placeholder}
                className={openTextClass}
                multiline
            />
        </OpenFeedbackBox>
    );
}
